import { withPrep } from "../lib/issuer";
import { LegalFooter } from "./Legal";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Download, XCircle, AlertCircle } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import { api } from "../lib/api";
import { euro, daysLeft } from "../lib/format";
import { computeTotals, initSelections, earlyActiveNow } from "../lib/calc";
import { generateQuotePdf } from "../lib/pdf";
import PaymentSection from "../components/PaymentSection";
import PublicProgress from "../components/PublicProgress";
import { Button } from "../components/ui/button";
import { proposalTotalsInput } from "../components/proposal/proposalShared";
import ProposalStatusBanners from "../components/proposal/ProposalStatusBanners";
import ProposalHeaderCard from "../components/proposal/ProposalHeaderCard";
import ProposalPackageGroups from "../components/proposal/ProposalPackageGroups";
import ProposalServiceItems from "../components/proposal/ProposalServiceItems";
import ProposalExtras from "../components/proposal/ProposalExtras";
import ProposalSummaryBody from "../components/proposal/ProposalSummaryBody";
import AcceptancePanel from "../components/proposal/AcceptancePanel";
import RejectDialog from "../components/proposal/RejectDialog";

const brandLogoCache = {};

async function toDataUrl(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export default function PublicProposal({ previewData = null }) {
  const params = useParams();
  const token = previewData ? previewData.public_token : params.token;
  const readOnly = !!previewData;
  const [quote, setQuote] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selections, setSelections] = useState({});
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [hasSignature, setHasSignature] = useState(false);
  const signatureRef = useRef(null);
  const [agreed, setAgreed] = useState(false);
  const [agreedVexatious, setAgreedVexatious] = useState(false);
  const [agreedEarly, setAgreedEarly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectName, setRejectName] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [renewRequested, setRenewRequested] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const paymentRef = useRef(null);
  const goToPayment = () => {
    setTimeout(() => paymentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const locked = quote?.status === "accettato" || quote?.status === "rifiutato" || quote?.status === "scaduto";
  const logoUrl = quote?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${quote.logo_url}` : null;
  const cur = quote?.currency || "EUR";
  const earlyActive = quote
    ? earlyActiveNow(quote.early_discount_type, quote.early_discount_deadline)
    : false;

  const isPreview = new URLSearchParams(window.location.search).get("preview") === "admin";
  const track = (type, message, amount) => readOnly ? Promise.resolve() :
    api.post(`/public/quotes/${token}/event`, { type, message, amount, preview: isPreview }).catch(() => {});

  useEffect(() => {
    if (readOnly) {
      setQuote(previewData);
      setSelections(previewData.client_selections || initSelections(previewData.items, previewData.groups));
      return;
    }
    api
      .get(`/public/quotes/${token}`)
      .then((r) => {
        setQuote(r.data);
        setSelections(r.data.client_selections || initSelections(r.data.items, r.data.groups));
        api.post(`/public/quotes/${token}/visit`, { preview: isPreview }).catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [token, readOnly, previewData, isPreview]);

  useEffect(() => {
    if (readOnly || quote?.status !== "accettato" || !quote?.progress_enabled) return;
    const iv = setInterval(() => {
      api.get(`/public/quotes/${token}`).then((r) => setQuote(r.data)).catch(() => {});
    }, 20000);
    return () => clearInterval(iv);
  }, [quote?.status, quote?.progress_enabled, token, readOnly]);

  const totals = useMemo(() => (quote ? computeTotals(proposalTotalsInput(quote, selections, promo)) : null), [quote, selections, promo]);
  const totalsNoPromo = useMemo(() => (quote && promo ? computeTotals(proposalTotalsInput(quote, selections, null)) : null), [quote, selections, promo]);
  const earlyVariants = useMemo(() => {
    if (!quote || !quote.early_discount_type || quote.early_discount_type === "none") return null;
    const common = proposalTotalsInput(quote, selections, promo);
    return { full: computeTotals({ ...common, early_active: false }).netto, early: computeTotals({ ...common, early_active: true }).netto };
  }, [quote, selections, promo]);

  const toggleOptional = (id, val) => {
    setSelections((s) => ({ ...s, [id]: { ...s[id], enabled: val } }));
    const it = quote?.items?.find((x) => x.id === id);
    if (it)
      track("TOGGLE_ADDON",
        `Ha ${val ? "attivato" : "disattivato"} l'opzione ${it.title} (${val ? "+" : "-"} ${euro(it.unit_price, cur)})`,
        it.unit_price);
  };

  const changeQty = (item, delta) =>
    setSelections((s) => {
      const cur = s[item.id]?.quantity ?? item.quantity;
      const next = Math.max(item.min_qty || 1, Math.min(item.max_qty || 999, cur + delta));
      return { ...s, [item.id]: { ...s[item.id], quantity: next } };
    });

  const selectPackage = (groupId, pkgId) => {
    setSelections((s) => ({ ...s, [groupId]: { package: pkgId } }));
    const g = quote?.groups?.find((x) => x.id === groupId);
    const p = g?.packages?.find((x) => x.id === pkgId);
    if (p) track("CAMBIO_TIER", `Ha selezionato il pacchetto ${p.name} (${euro(p.price, cur)})`, p.price);
  };

  const inRevision = quote?.status === "modifica_richiesta";
  const negotiable = (quote?.items || []).some((it) => it.item_type === "variable") && ["inviato", "visualizzato"].includes(quote?.status);
  const proposalChanges = (quote?.items || [])
    .filter((it) => it.item_type === "variable" && Number(selections[it.id]?.quantity ?? it.quantity) !== Number(it.quantity))
    .map((it) => [it.id, Number(selections[it.id].quantity)]);
  const resetQuantities = () => setSelections((s) => {
    const n = { ...s };
    (quote?.items || []).forEach((it) => { if (it.item_type === "variable") n[it.id] = { ...n[it.id], quantity: it.quantity }; });
    return n;
  });
  const sendRevision = async ({ message, name }) => {
    const { data } = await api.post(`/public/quotes/${token}/request-revision`, {
      quantities: Object.fromEntries(proposalChanges.map(([id, v]) => [id, Number(v)])), message, name });
    const r = await api.get(`/public/quotes/${token}`);
    setQuote(r.data);
    return data;
  };

  const hasProposal = negotiable && proposalChanges.length > 0;
  const issuerName = quote?.issuer?.display_name || "Il Professionista";

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const hasVexatious = !!(quote?.vexatious_clauses || "").trim();
  const canAccept = firstName.trim() && lastName.trim() && emailValid && agreed && hasSignature && (!hasVexatious || agreedVexatious) && (!earlyActive || agreedEarly);

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoBusy(true);
    try {
      const { data } = await api.post(`/public/quotes/${token}/validate-promo`, { code });
      setPromo(data);
      toast.success(`Codice "${data.code}" applicato!`);
      track("CODICE_PROMO", `Ha applicato il codice promo ${data.code}`);
    } catch (e) {
      setPromo(null);
      toast.error(e.response?.data?.detail || "Codice non valido");
    }
    setPromoBusy(false);
  };

  const removePromo = () => {
    setPromo(null);
    setPromoInput("");
  };

  const accept = async () => {
    if (!firstName.trim()) return toast.error("Inserisci il nome");
    if (!lastName.trim()) return toast.error("Inserisci il cognome");
    if (!emailValid) return toast.error("Inserisci un'email valida");
    if (!agreed) return toast.error("Devi accettare il preventivo e i Termini e Condizioni generali");
    if (hasVexatious && !agreedVexatious) return toast.error("Devi approvare specificamente le clausole ex artt. 1341 e 1342 c.c.");
    const signature = signatureRef.current?.getSignature?.() || "";
    if (!signature) return toast.error("Aggiungi la tua firma");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/quotes/${token}/accept`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        role: role.trim(),
        signature,
        accept_general: agreed,
        accept_vexatious: agreedVexatious,
        accept_early: agreedEarly,
        promo_code: promo?.code || "",
        selections,
      });
      setQuote(data);
      toast.success("Preventivo accettato e firmato con successo! 🎉");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'accettazione");
    }
    setSubmitting(false);
  };

  const reject = async () => {
    setRejecting(true);
    try {
      const { data } = await api.post(`/public/quotes/${token}/reject`, {
        name: rejectName.trim(),
        reason: rejectReason.trim(),
      });
      setQuote(data);
      setRejectOpen(false);
      toast("Preventivo rifiutato", { description: "L'emittente è stato avvisato." });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante il rifiuto");
    }
    setRejecting(false);
  };

  const downloadPdf = async () => {
    const logo = logoUrl ? await toDataUrl(logoUrl) : null;
    if (quote.watermark && brandLogoCache.value === undefined) {
      brandLogoCache.value = await toDataUrl(`${window.location.origin}/logo-header@3x.png`);
    }
    const brandLogo = quote.watermark ? brandLogoCache.value : null;
    generateQuotePdf({ quote, issuer: quote.issuer, totals, logo, brandLogo });
    track("DOWNLOAD_PDF", "Ha scaricato la copia PDF del preventivo");
  };

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6 text-center">
        <AlertCircle className="w-10 h-10 text-slate-300 mb-3" />
        <h1 className="text-xl font-bold text-slate-900">Preventivo non trovato</h1>
        <p className="text-sm text-slate-500 mt-1">Il link potrebbe essere errato o scaduto.</p>
      </div>
    );

  if (!quote || !totals)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );

  const dl = daysLeft(quote.due_date);

  return (
    <div className={`min-h-screen bg-background ${readOnly ? "pointer-events-none select-none" : ""}`} data-testid={readOnly ? "public-proposal-readonly" : undefined}>
      <div className="h-1.5 bg-primary" />
      {quote.qty_unavailable && (
        <div data-testid="qty-unavailable-banner" role="alert" className="bg-red-50 border-b border-red-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-start gap-2.5 text-red-800">
            <XCircle style={{ width: 18, height: 18 }} className="shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold">Spiacenti, quantità non disponibile</p>
              {quote.qty_unavailable.note && <p data-testid="qty-unavailable-note" className="text-red-700">{quote.qty_unavailable.note}</p>}
              <p className="text-red-700/80">Puoi firmare il preventivo con le quantità originali proposte {withPrep("da", issuerName)}.</p>
            </div>
          </div>
        </div>
      )}
      {isPreview && (
        <div data-testid="admin-preview-bar" className="bg-slate-900 text-white text-sm px-4 py-2.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-center">
          <span>Modalità Anteprima Professionista — Le tue visite e modifiche non vengono registrate nel Registro Attività</span>
          <button
            data-testid="simulate-visit-button"
            onClick={() => {
              api.post(`/public/quotes/${token}/visit`, { preview: false }).then(() => {
                toast.success("Evento visita cliente simulato e registrato");
              });
            }}
            className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 transition-colors rounded-full px-3 py-1 text-xs font-semibold"
          >
            Simula Evento Visita Cliente
          </button>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Top bar */}
        <div className={`flex items-center mb-8 ${quote.watermark ? "justify-between" : "justify-end"}`}>
          {quote.watermark && (
            <a href="https://myquoteflow.it" target="_blank" rel="noreferrer" data-testid="public-brand-logo-link" className="flex items-center">
              <BrandLogo size="md" />
            </a>
          )}
          <Button variant="outline" size="sm" data-testid="public-export-pdf-button" onClick={downloadPdf}>
            <Download style={{ width: 15, height: 15 }} className="mr-1.5" /> Scarica PDF
          </Button>
        </div>

        <ProposalStatusBanners {...{ cur, earlyActive, goToPayment, issuerName, locked, quote, renewRequested, renewing, setRenewRequested, setRenewing, token, totals }} />

        {/* Header card */}
        <ProposalHeaderCard {...{ dl, issuerName, locked, logoUrl, quote }} />

        {/* Two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Services */}
          <div className="lg:col-span-7 xl:col-span-8 space-y-4">
            <ProposalPackageGroups {...{ cur, locked, quote, selectPackage, selections }} />

            {quote.status === "accettato" && quote.progress_enabled && (
              <PublicProgress quote={quote} cur={cur} onGoTo={goToPayment} />
            )}



            <ProposalServiceItems {...{ changeQty, cur, locked, quote, selections, toggleOptional }} />
            <ProposalExtras {...{ cur, hasVexatious, inRevision, issuerName, negotiable, proposalChanges, quote, resetQuantities, sendRevision, totals }} />
          </div>

          {/* Sticky summary */}
          <div className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-6 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-card/90 backdrop-blur shadow-lg overflow-hidden">
              <div className="bg-slate-900 px-6 py-5 text-white">
                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Netto a Pagare</p>
                {totalsNoPromo && totalsNoPromo.netto > totals.netto && (
                  <p data-testid="public-summary-old-total" className="font-mono text-base text-slate-400 line-through mt-1">
                    {euro(totalsNoPromo.netto, cur)}
                  </p>
                )}
                <p key={totals.netto} className="font-mono text-3xl font-extrabold mt-0.5 qf-pop" data-testid="public-summary-grand-total">
                  {euro(totals.netto, cur)}
                </p>
                {totals.scontoPromo > 0 && (
                  <p data-testid="public-summary-promo-savings" className="text-[12px] font-semibold text-emerald-300 mt-1">
                    Codice {promo?.code} applicato · risparmi {euro(totals.scontoPromo, cur)}
                  </p>
                )}
                {totals.ritenuta > 0 && (
                  <p className="text-[11px] text-slate-400 mt-1">Bonifico effettivo (al netto della ritenuta)</p>
                )}
              </div>
              <ProposalSummaryBody {...{ applyPromo, cur, earlyActive, earlyVariants, locked, promo, promoBusy, promoInput, quote, removePromo, setPromoInput, totals }} />

              {/* Acceptance */}
              <AcceptancePanel {...{ accept, agreed, agreedEarly, agreedVexatious, canAccept, cur, earlyActive, earlyVariants, email, firstName, hasProposal, hasVexatious, inRevision, issuerName, lastName, locked, quote, reject, resetQuantities, role, sendRevision, setAgreed, setAgreedEarly, setAgreedVexatious, setEmail, setFirstName, setHasSignature, setLastName, setRejectOpen, setRole, signatureRef, submitting }} />
            </div>
          </div>
        </div>

        <div className="mt-10 max-w-3xl">
          <PaymentSection ref={paymentRef} quote={quote} cur={cur} isPreview={isPreview} onUpdated={(data) => setQuote(data)} />
        </div>
        {!isPreview && <LegalFooter className="mt-14 pt-6 border-t border-slate-200" />}
      </div>

      <RejectDialog {...{ reject, rejectName, rejectOpen, rejectReason, rejecting, setRejectName, setRejectOpen, setRejectReason }} />
    </div>
  );
}
